"use client";

import { useRouter } from "next/navigation";
import React, { ReactElement, useEffect } from "react";

import withQuoteData from "@/components/with-quote-data";
import Styles from "@/components/wrappers/renewal/confirmation-page-wrapper.module.css";
import { APPLICATION, MY_ACCOUNT_URL, PRODUCT } from "@/constants";
import { brand } from "@/tailwind.config";

interface RenewalCurrentPolicyWrapperProps {
  headerTextSection?: ReactElement;
  headerImageSection?: ReactElement;
  headerImageSectionAvn?: ReactElement;
  policyDetailsSection?: ReactElement;
  emergencyHelpSection?: ReactElement;
  claimSection?: ReactElement;
  refferFriendSection?: ReactElement;
  btaVotingSection?: ReactElement;
  nhsSection?: ReactElement;
  trustPilotSection?: ReactElement;
  airDoctorAssistantSection?: ReactElement;
}

function ConfirmationPageWrapper({
  claimSection,
  emergencyHelpSection,
  headerImageSection,
  headerImageSectionAvn,
  headerTextSection,
  nhsSection,
  policyDetailsSection,
  refferFriendSection,
  btaVotingSection,
  trustPilotSection,
  airDoctorAssistantSection,
}: Readonly<RenewalCurrentPolicyWrapperProps>) {
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (APPLICATION === "renewal") {
        if (window.location.pathname.startsWith("/confirmation")) {
          const confirmationLoaded =
            sessionStorage.getItem("confirmationLoaded");
          if (confirmationLoaded) {
            window.location.href = MY_ACCOUNT_URL!;
          } else {
            sessionStorage.setItem("confirmationLoaded", "true");
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      router.push(process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!);
    };

    const isOnIOS =
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPhone/i);
    const eventName = isOnIOS ? "pagehide" : "beforeunload";

    // window.addEventListener(eventName, handleBeforeUnload);
    return () => {
      return window.removeEventListener(eventName, handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-surface-neutral-option-2">
      <div className="lg:mx-spacing-6xl md:mx-[48px] mx-[24px] min-h-[calc(100vh-12px)] md:py-spacing-xxl py-spacing-l">
        {PRODUCT === "avanti" ? (
          <header className="grid lg:grid-cols-12 lg:col-span-12 lg:order-1 gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l h-fit">
            <aside
              className={`${Styles.bannerBg} text-text-functional rounded-radius-xxl lg:p-spacing-l font-body-font lg:col-span-12 lg:order-1 border-white border-2 h-[196px] lg:h-full`}
            >
              {headerImageSectionAvn}
            </aside>
            <aside className="bg-surface-neutral-option-1 rounded-radius-xxl p-spacing-m md:p-spacing-l font-body-font lg:col-span-12 lg:order-2 lg:hidden">
              {headerTextSection}
            </aside>
          </header>
        ) : (
          <header className="relative grid grid-cols-12 lg:gap-spacing-l md:gap-spacing-xl gap-spacing-m break-words">
            <aside className="relative overflow-hidden h-[88px] lg:h-full rounded-2xl lg:col-span-5 col-span-12 lg:order-2">
              {headerImageSection}
            </aside>
            <aside
              className="bg-surface-neutral-option-1 lg:bg-dark-surface-accent-1-primary text-text-body 

               lg:text-dark-text-title rounded-radius-xxl p-spacing-m md:p-spacing-l font-body-font lg:col-span-7 col-span-12 lg:order-1"
            >
              {headerTextSection}
            </aside>
          </header>
        )}

        <main className="grid grid-cols-12 lg:gap-spacing-l md:gap-spacing-xl gap-spacing-m lg:mt-spacing-xl">
          <aside className="lg:gap-spacing-xxl md:gap-spacing-xl gap-spacing-l lg:col-span-7 col-span-12 ">
            <section className="rounded-radius-xxl bg-dark-surface-accent-1-primary h-fit mt-spacing-m md:mt-spacing-xl lg:mt-0 p-spacing-m lg:p-spacing-xl md:p-spacing-xxl">
              {policyDetailsSection}
            </section>
            {btaVotingSection}
            <section
              className={`${brand} ${Styles.referFriendBg} rounded-radius-xxl md:relative md:overflow-hidden md:h-[308px] bg-surface-accent-1-secondary mt-spacing-m md:mt-spacing-xl`}
            >
              {refferFriendSection}
            </section>
          </aside>
          <aside className="lg:gap-spacing-xxl md:gap-spacing-xl gap-spacing-l lg:col-span-5 col-span-12">
            <section className="rounded-radius-xxl bg-surface-neutral-option-1 p-spacing-m md:p-spacing-xxl lg:p-spacing-xl">
              {emergencyHelpSection}
            </section>

            {/**Air Doctor Implementation */}
            {PRODUCT === "staysure" && (
              <section
                className={`rounded-radius-xxl bg-surface-neutral-option-1 p-spacing-m md:p-spacing-xxl lg:p-spacing-xl mt-spacing-m md:mt-spacing-xl lg:mt-spacing-l`}
              >
                {airDoctorAssistantSection}
              </section>
            )}

            <section className="rounded-radius-xxl bg-surface-neutral-option-1 mt-spacing-m md:mt-spacing-xl p-spacing-m md:p-spacing-xxl lg:p-spacing-xl">
              {claimSection}
            </section>
            {PRODUCT === "staysure" && (
              <section className="rounded-radius-xxl bg-surface-promo mt-spacing-m md:mt-spacing-xl px-spacing-m py-spacing-l md:p-spacing-xl">
                {nhsSection}
              </section>
            )}

            <section className="rounded-radius-xxl bg-surface-neutral-option-1 mt-spacing-m md:mt-spacing-xl">
              {trustPilotSection}
            </section>
          </aside>
        </main>
      </div>
    </div>
  );
}

export default withQuoteData(ConfirmationPageWrapper, "policy");
